import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { TriangleAlert, UserPlus, Users } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { CampoControlado, Formulario, Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { cabecalhoEmpresa, get, mensagemDeErro, post } from '../../nucleo/api';
import { campoEmpresaDoRegistro, empresaEscolhida } from '../../nucleo/empresa/empresa-do-registro';
import { textoInadimplencia, type MarcaInadimplencia } from '../../nucleo/inadimplencia';
import { comMaisOpcoes } from '../../nucleo/modo-simples';
import { useSessao } from '../../nucleo/sessao';
import { opcoesDeCategoria, useCategoriasFin, useCentrosFin, useFormasPagamento } from './dados';
import { NovoFornecedor } from '../../componentes/novo-fornecedor';
import { ItensEditor, itensParaApi, novoItem, type ItemForm } from './ItensEditor';
import { novaLinhaRateio, RateioEditor, rateioParaApi, type LinhaRateio } from './RateioEditor';
import { hojeLocal, invalidarContas, type TipoDocumento } from './tipos';

/**
 * Fase 2 (W19, K4, decisão 15): o cliente escolhido na fatura está inadimplente? A marca vem do Cadastros (lista com `ids`),
 * que a pede ao Contas só para quem tem `contas.ver`. Aviso que NÃO bloqueia a emissão.
 */
function useInadimplencia(parceiroId: string | null, ativo: boolean) {
  const q = useQuery({
    queryKey: ['/cadastros/parceiros', 'inadimplencia', parceiroId],
    queryFn: () => get<{ itens: { id: string; inadimplencia?: MarcaInadimplencia | null }[] }>('/cadastros/parceiros', { ids: parceiroId, limite: 1 }),
    enabled: ativo && !!parceiroId,
    staleTime: 30_000,
  });
  return ativo && parceiroId ? (q.data?.itens.find((i) => i.id === parceiroId)?.inadimplencia ?? null) : null;
}

/**
 * Drawer de novo documento (M5.1). O id nasce aqui e vale como Idempotency-Key: reenviar o mesmo formulário
 * (clique duplo, nova tentativa depois de erro) devolve o mesmo documento, nunca um segundo.
 */
export function FormDocumento({ aberto, tipos, aoFechar, aoSalvar }: { aberto: boolean; tipos: TipoDocumento[]; aoFechar: () => void; aoSalvar?: (ids: string[]) => void }) {
  if (!aberto) return null;
  return <Corpo tipos={tipos} aoFechar={aoFechar} aoSalvar={aoSalvar} />;
}

function Corpo({ tipos, aoFechar, aoSalvar }: { tipos: TipoDocumento[]; aoFechar: () => void; aoSalvar?: (ids: string[]) => void }) {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const s = useSessao();
  const categorias = useCategoriasFin();
  const centros = useCentrosFin();
  const [novoId] = useState(() => crypto.randomUUID());
  const [itens, setItens] = useState<ItemForm[]>([novoItem()]);
  const [tipo, setTipo] = useState<TipoDocumento>(tipos[0]!);
  // Fase 2 (W14): rateio por centro de custo no lugar do centro único (a API aceita desde a W7).
  const [ratear, setRatear] = useState(false);
  const [rateio, setRateio] = useState<LinhaRateio[]>(() => [novaLinhaRateio(), novaLinhaRateio()]);
  const opcoesCentros = (centros.data?.itens ?? []).filter((c) => c.ativo).map((c) => ({ valor: c.id, rotulo: c.nome }));
  const moeda = s.workspace.moeda;
  const receber = tipo === 'fatura';
  const [parceiroId, setParceiroId] = useState<string | null>(null);
  const inadimplencia = useInadimplencia(parceiroId, receber && s.pode('contas.ver') && s.pode('cadastros.ver'));
  // Fase 2 (W24, K9, F13): o parceiro sai do Formulario para o atalho "Diversos" e o cadastro rápido (o do Compras) o trocarem.
  const [parceiroRotulo, setParceiroRotulo] = useState('');
  const [erroParceiro, setErroParceiro] = useState<string | null>(null);
  const [rapido, setRapido] = useState(false);
  const [buscandoDiversos, setBuscandoDiversos] = useState(false);
  const pagar = !receber;
  const atalhos = pagar && s.pode('cadastros.criar');
  const formas = useFormasPagamento();
  // A busca guarda o rótulo do escolhido; trocar por fora (Diversos, cadastro rápido) remonta o campo com o rótulo novo.
  const [versaoParceiro, setVersaoParceiro] = useState(0);
  const escolherParceiro = (id: string | null, rotulo = '', externo = false) => {
    setParceiroId(id);
    setParceiroRotulo(rotulo);
    setErroParceiro(null);
    if (externo) setVersaoParceiro((n) => n + 1);
  };
  async function usarDiversos() {
    setBuscandoDiversos(true);
    try {
      const p = await post<{ id: string; nome: string }>('/contas/parceiro-diversos', {});
      escolherParceiro(p.id, p.nome, true);
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    } finally {
      setBuscandoDiversos(false);
    }
  }

  const campos: DefCampo[] = [
    // V2 verticais: sem empresa na aba (sem filtro, várias permitidas), o documento pede a empresa.
    ...campoEmpresaDoRegistro(s.empresas, { rotulo: tn('empresa.rotulo'), ajuda: tn('empresa.ajudaRegistro') }),
    { nome: 'emissao', rotulo: t('campos.emissao'), tipo: 'data', obrigatorio: true, largura: 'terco' },
    { nome: 'condicao_pagamento', rotulo: t('campos.condicao'), tipo: 'texto', largura: 'terco', placeholder: t('w7.condicaoPlaceholder'), ajuda: t('ajuda.condicao') },
    { nome: 'vencimento', rotulo: t('campos.vencimento'), tipo: 'data', largura: 'terco', ajuda: t('ajuda.vencimento'), visivel: (v) => !/\d/.test(String(v.condicao_pagamento ?? '')) },
    // Fase 2, W7 (E-5): competência própria (vazio vale a emissão) e centro de custo do documento.
    { nome: 'competencia', rotulo: t('w7.competencia'), tipo: 'data', largura: 'terco', ajuda: t('w7.ajudaCompetencia') },
    { nome: 'centro_custo_id', rotulo: t('w7.centroCusto'), tipo: 'selecao', largura: 'terco', placeholder: t('w7.semCentro'), ajuda: t('w7.ajudaCentro'), opcoes: opcoesCentros, visivel: () => !ratear },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'texto' },
    // Fase 2 (W24, K9, P4): forma de pagamento prevista (catálogo do Cadastros); a baixa usa esta se não escolher outra.
    ...(formas.habilitado
      ? [{ nome: 'forma_pagamento_id', rotulo: t('w24.formaPagamento'), tipo: 'selecao', largura: 'terco', placeholder: t('w24.semForma'), opcoes: formas.opcoes, ajuda: t('w24.ajudaForma') } satisfies DefCampo]
      : []),
    { nome: 'desconto_centavos', rotulo: t('campos.desconto'), tipo: 'dinheiro', largura: 'terco', ajuda: t('ajuda.desconto') },
    { nome: 'juros_mes_pct', rotulo: t('campos.jurosMes'), tipo: 'percentual', largura: 'terco', min: 0, max: 100, ajuda: t('ajuda.encargos') },
    { nome: 'multa_pct', rotulo: t('campos.multa'), tipo: 'percentual', largura: 'terco', min: 0, max: 100 },
  ];

  // U15 F5: no modo simples, só o essencial à vista (cliente, datas, itens); o resto fica em "Mais opções" (emissão, condição, descrição,
  // competência, centro de custo, forma de pagamento, desconto, juros, multa e o rateio). A emissão é obrigatória, mas nasce com a
  // data de hoje (valor padrão), então pode ficar lá; o cliente e os itens, sem padrão, ficam sempre à vista.
  const inicialDoc = { emissao: hojeLocal(), condicao_pagamento: '' };
  const maisOpcoes = comMaisOpcoes(campos, {
    ativo: s.modoSimples,
    avancados: ['emissao', 'condicao_pagamento', 'descricao', 'competencia', 'centro_custo_id', 'forma_pagamento_id', 'desconto_centavos', 'juros_mes_pct', 'multa_pct'],
    rotulo: tn('comum.maisOpcoes'),
    inicial: inicialDoc,
  });
  const rateioEditor = <RateioEditor ativo={ratear} aoAtivar={setRatear} linhas={rateio} aoMudar={setRateio} centros={opcoesCentros} />;

  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={t(`acoes.novo.${tipo}`)}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-documento">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      {tipos.length > 1 && (
        <div className="mb-4">
          <Rotulo id="tipo-documento" rotulo={t('campos.tipo')} obrigatorio>
            <Selecao id="tipo-documento" opcoes={tipos.map((x) => ({ valor: x, rotulo: t(`tipo.${x}`) }))} value={tipo} onChange={(e) => {
                setTipo(e.target.value as TipoDocumento);
                setItens((l) => l.map((i) => ({ ...i, categoria_id: '' })));
              }} />
          </Rotulo>
        </div>
      )}
      <div className="mb-4 flex min-w-0 items-end gap-2">
        <div className="min-w-0 flex-1">
          <CampoControlado
            key={versaoParceiro}
            def={{
              nome: 'parceiro_id',
              rotulo: receber ? t('campos.cliente') : t('campos.fornecedor'),
              tipo: 'busca',
              obrigatorio: true,
              busca: { caminho: '/cadastros/parceiros', params: { papel: receber ? 'cliente' : 'fornecedor', operacao: true }, rotulo: (i) => String(i.nome) },
            }}
            valor={parceiroId}
            rotuloAtual={parceiroRotulo}
            aoMudar={(v) => escolherParceiro((v as string | null) || null)}
            erro={erroParceiro ?? undefined}
          />
        </div>
        {pagar && s.pode('contas.criar') && (
          <Botao className="mb-px shrink-0" icone={<Users className="size-4" />} carregando={buscandoDiversos} onClick={() => void usarDiversos()} title={t('w24.usarDiversos')}>
            <span className="hidden sm:inline">{t('w24.diversos')}</span>
            <span className="sr-only sm:hidden">{t('w24.usarDiversos')}</span>
          </Botao>
        )}
        {atalhos && (
          <Botao className="mb-px shrink-0" icone={<UserPlus className="size-4" />} onClick={() => setRapido(true)}>
            <span className="hidden sm:inline">{tn('fornecedorRapido.titulo')}</span>
            <span className="sr-only sm:hidden">{tn('fornecedorRapido.titulo')}</span>
          </Botao>
        )}
      </div>
      {inadimplencia && (
        <p role="status" data-aviso-inadimplente="" className="mb-4 flex items-start gap-2 rounded-lg border border-laranja px-3 py-2 text-sm text-tinta-2">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-laranja" aria-hidden />
          <span>{tn('inadimplencia.aviso', { detalhe: textoInadimplencia(inadimplencia) })}</span>
        </p>
      )}
      <Formulario
        key={tipo}
        id="form-documento"
        campos={maisOpcoes.campos}
        gruposRecolhiveis={maisOpcoes.gruposRecolhiveis}
        recolhiveisAoFim
        fimDoGrupo={maisOpcoes.gruposRecolhiveis ? { [maisOpcoes.gruposRecolhiveis[0]!]: rateioEditor } : undefined}
        entidade="contas.documento"
        inicial={inicialDoc}
        aoEnviar={async (v) => {
          if (!parceiroId) {
            setErroParceiro(t(receber ? 'w24.clienteObrigatorio' : 'w24.parceiroObrigatorio'));
            return;
          }
          const itensApi = itensParaApi(itens, { semItens: t('erros.semItens'), incompleto: t('erros.itemIncompleto') });
          const rateioApi = ratear ? rateioParaApi(rateio, { incompleto: t('w14.rateioIncompleto'), repetido: t('w14.rateioRepetido') }) : null;
          const r = await post<{ id: string; ids: string[] }>(
            '/contas/documentos',
            {
              id: novoId,
              tipo,
              parceiro_id: parceiroId,
              forma_pagamento_id: v.forma_pagamento_id || null,
              itens: itensApi,
              emissao: v.emissao || undefined,
              competencia: v.competencia || undefined,
              centro_custo_id: ratear ? null : v.centro_custo_id || null,
              rateio: rateioApi,
              vencimento: v.vencimento || undefined,
              condicao_pagamento: String(v.condicao_pagamento ?? '').trim() || undefined,
              descricao: String(v.descricao ?? '').trim() || undefined,
              desconto_centavos: (v.desconto_centavos as number | null) || undefined,
              juros_mes_pct: (v.juros_mes_pct as number | null) ?? undefined,
              multa_pct: (v.multa_pct as number | null) ?? undefined,
              extras: v.extras ?? {},
            },
            { 'Idempotency-Key': novoId, ...cabecalhoEmpresa(empresaEscolhida(v)) },
          );
          await invalidarContas(qc);
          toast.sucesso(r.ids.length > 1 ? t('mensagens.emitidoVarios', { count: r.ids.length }) : tn('comum.salvo'));
          aoSalvar?.(r.ids);
          aoFechar();
        }}
      >
        <ItensEditor itens={itens} aoMudar={setItens} categorias={opcoesDeCategoria(categorias.data?.itens ?? [], receber)} moeda={moeda} />
        {!maisOpcoes.gruposRecolhiveis && rateioEditor}
      </Formulario>
      {/* Fase 2 (W24, K9, F13): o cadastro rápido de fornecedor do Compras (nome e documento opcional). */}
      <NovoFornecedor aberto={rapido} origem="contas" aoFechar={() => setRapido(false)} aoCriar={(f) => escolherParceiro(f.id, f.nome, true)} />
    </Drawer>
  );
}
